import React, { FC, Fragment, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import styles from "./Forms.module.css";

import type {
    AddDeliveryAddressModalFormProps,
    ICreateDeliveryAddressFormData,
} from "@model/DeliveryAddress.model";

import { DeliveryAddressService } from "Logic/DeliveryAddressService";
import { defaultDeliveryAddressValues } from "Utils/Global/globalConstants";

import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import CheckBox from "../UILibrary/CheckBox";

import { useForceLogoutAndRedirect } from "Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";
import { useNavigate } from "react-router-dom";

const ADD_INFO_MAX = 500;

const AddDeliveryAddressModalForm: FC<AddDeliveryAddressModalFormProps> = ({
    token,
    onClose,
    onCreated,
}) => {
    const { control, handleSubmit, reset, watch } = useForm<ICreateDeliveryAddressFormData>({
        mode: "onSubmit",
        defaultValues: defaultDeliveryAddressValues,
    });
    const service = new DeliveryAddressService();
    const [apiError, setApiError] = useState<string | undefined>(undefined);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();
    const { showPopup } = UnauthorizedPopupStoreImplementation();
    const onAuthError = useForceLogoutAndRedirect();
    const additionalInfoValue = watch("additionalInformation") ?? "";
    const additionalInfoCount = additionalInfoValue.trimEnd().length; 

    const onValidSubmit = async (values: ICreateDeliveryAddressFormData) => {
        if (submitting) return;

        const message = await service.handleCreateDeliveryAddress({
            data: values,
            token,
            setLoading: setSubmitting,
            setApiError: (m) => setApiError(m ?? undefined),
            reset,
            onAuthError,
            onForbidden: (msg) => {
                showPopup(msg);
                navigate("/", { replace: true });
            },
        });

        if (message) {
            onClose();
            onCreated();
        }

    };

    return (
        <form
            className={styles.mcdForm}
            data-testid="add-address-form"
            onSubmit={handleSubmit(onValidSubmit)}
        >
            <div className={styles.mcdRow}>
                <Controller
                    name="addressLabel"
                    control={control}
                    rules={{ required: "Address label is required." }}
                    render={({ field, fieldState }) => (
                        <TextInput
                            label="Address label"
                            placeholder="e.g. Home, Office…"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                            data-testid="addressLabel"
                        />
                    )}
                />

                <Controller
                    name="houseNumber"
                    control={control}
                    rules={{ required: "House number is required." }}
                    render={({ field, fieldState }) => (
                        <TextInput
                            label="House number"
                            placeholder="e.g. 12A"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                            data-testid="houseNumber"
                        />
                    )}
                />
            </div>

            <Controller
                name="street"
                control={control}
                rules={{ required: "Street is required." }}
                render={({ field, fieldState }) => (
                    <TextInput
                        label="Street"
                        placeholder="Street name…"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={submitting}
                        error={fieldState.error?.message}
                        data-testid="street"
                    />
                )}
            />

            <div className={styles.mcdRow}>
                <Controller
                    name="city"
                    control={control}
                    rules={{ required: "City is required." }}
                    render={({ field, fieldState }) => (
                        <TextInput
                            label="City"
                            placeholder="City…"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                            data-testid="city"
                        />
                    )}
                />

                <Controller
                    name="region"
                    control={control}
                    rules={{ required: "Region is required." }}
                    render={({ field, fieldState }) => (
                        <TextInput
                            label="Region"
                            placeholder="Region…"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                            data-testid="region"
                        />
                    )}
                />
            </div>

            <Controller
                name="country"
                control={control}
                rules={{ required: "Country is required." }}
                render={({ field, fieldState }) => (
                    <TextInput
                        label="Country"
                        placeholder="Country…"
                        value={field.value || ""}
                        onChange={field.onChange}
                        disabled={submitting}
                        error={fieldState.error?.message}
                        data-testid="country"
                    />
                )}
            />

            <div className={styles.mcdRow}>
                <Controller
                    name="postalCode"
                    control={control}
                    render={({ field, fieldState }) => (
                        <TextInput
                            label="Postal code (optional)"
                            placeholder="Postal code…"
                            value={field.value || ""}
                            onChange={field.onChange}
                            disabled={submitting}
                            error={fieldState.error?.message}
                            data-testid="postalCode"
                        />
                    )}
                />

                <Controller
                    name="isDefaultAddress"
                    control={control}
                    render={({ field, fieldState }) => (
                        <CheckBox
                            value={Boolean(field.value)}
                            onChange={field.onChange}
                            message="Set as default"
                            errorMessage={fieldState.error?.message}
                            disableCheckBox={submitting}
                            data-testid="isDefaultAddress"
                        />
                    )}
                />
            </div>

            <Controller
                name="additionalInformation"
                control={control}
                rules={{
                    validate: (v) =>
                        (v?.trim().length ?? 0) <= ADD_INFO_MAX ||
                        `Must be at most ${ADD_INFO_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                    <Fragment>
                        <label className={styles.mcdInputLabel} htmlFor="addr-additional">
                            Additional information (optional)
                        </label>

                        <textarea
                            id="addr-additional"
                            className={styles.mcdTextarea}
                            rows={3}
                            placeholder="e.g. Ring the bell, call me when outside…"
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            disabled={submitting}
                            data-testid="additionalInformation"
                        />

                        <div className={styles.mcdCharCount} data-testid="additionalInformation-count">
                            {additionalInfoCount}/{ADD_INFO_MAX}
                        </div>

                        {fieldState.error?.message && (
                            <div className={styles.mcdErrorText} data-testid="additionalInformation-error">
                                {fieldState.error.message}
                            </div>
                        )}
                    </Fragment>
                )}
            />

            <div className={styles.mcdActions}>
                <button
                    type="button"
                    className={styles.mcdBtnGhost}
                    onClick={onClose}
                    disabled={submitting}
                    data-testid="add-address-cancel"
                >
                    Cancel
                </button>

                <Submission
                    error={apiError}
                    disabled={submitting}
                    loading={submitting}
                    buttonText="Save address"
                    className={styles.mcdBtnPrimary}
                />

            </div>
        </form>
    );
};

export default AddDeliveryAddressModalForm;
